import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';

import { ProtoImageConfig, ProtoImageType } from 'src/app/@shared/proto-image/proto-image.config';
import { ProtoImageModel, ProtoImageSource } from 'src/app/@shared/proto-image/proto-image.model';

const HUNDRED = 100;

@Component({
  selector: 'proto-image',
  standalone: false,
  styleUrls: ['./proto-image.component.scss'],
  templateUrl: './proto-image.component.html',
})

export class ProtoImageComponent implements OnInit {
  @ViewChild('ImageElement', {
    static: true,
  }) public imageElement: ElementRef;

  @Output() public imageClicked: EventEmitter<ProtoImageModel> = new EventEmitter<ProtoImageModel>();
  @Output() public imageLoaded: EventEmitter<ProtoImageModel> = new EventEmitter<ProtoImageModel>();
  @Output() public imageFailed: EventEmitter<ProtoImageModel> = new EventEmitter<ProtoImageModel>();

  // 'detail', 'avatar', avatar_large and 'thumbnail -> ratio based on its size defined in proto-image.config.ts
  // 'default' and 'original' -> no consistent ratio (auto height)
  @Input() public imageType: ProtoImageType = 'default';

  // should be true if imageUrl' is not defined
  @Input() public showPlaceholderImage: boolean;

  // useful when image is part of list item, which navigates to detail page
  @Input() public cachePreloadImage: boolean;

  public image: ProtoImageModel;
  public imageStatus: 'unset' | 'loading' | 'loaded' | 'error' = 'unset'; // TODO: move to proto-image.model ?

  private renderer = inject(Renderer2);

  // is set, when image is provided via external url
  // eslint-disable-next-line accessor-pairs
  @Input() set imageUrl(url: string) {
    // empty string is valid! (image upload/delete)
    if (!url && url !== '') {
      return;
    }

    this.setImage({
      imageUrl: url,
      type: this.imageType,
    });
  }

  public ngOnInit(): void {
    this.setCssDimensions();

    if (this.showPlaceholderImage) {
      this.renderer.setAttribute(this.imageElement.nativeElement, 'src', this.getFallbackImage('placeholder'));
    }
  }

  public onImageLoaded(): void {
    this.imageStatus = 'loaded';
    this.renderer.setStyle(this.imageElement.nativeElement, 'opacity', 1);
    this.imageLoaded.emit(this.image);
  }

  public onImageError(): void {
    console.error('image failed to load', this.image);
    this.imageStatus = 'error';
    this.imageFailed.emit(this.image);
    this.renderer.setAttribute(this.imageElement.nativeElement, 'src', this.getFallbackImage('error'));
  }

  public onImageClicked(): void {
    this.imageClicked.emit(this.image);
  }

  private setImage(source: Partial<ProtoImageSource>): void {
    // true when you delete an image
    if (!source.imageUrl) {
      return this.resetImage();
    }

    this.imageStatus = 'loading';
    this.image = new ProtoImageModel(source);

    return this.renderer.setAttribute(this.imageElement.nativeElement, 'src', this.image.urls[this.imageType]);
  }

  private resetImage(): void {
    if (this.showPlaceholderImage) {
      this.renderer.setAttribute(this.imageElement.nativeElement, 'src', this.getFallbackImage('placeholder'));
    } else {
      this.renderer.removeAttribute(this.imageElement.nativeElement, 'src');
      this.renderer.setStyle(this.imageElement.nativeElement, 'opacity', 0);
    }

    this.image = null;
    this.imageStatus = 'unset';
  }

  private getFallbackImage(type: 'placeholder' | 'error'): string {
    return ProtoImageConfig[type][this.imageType] || ProtoImageConfig[type].default;
  }

  private setCssDimensions(): void {
    const sizes = ProtoImageConfig.size;

    if (!sizes[this.imageType]) {
      // console.warn(this.imageType, 'has no dimensions in proto-image.config! We use "default" as fallback.');
      this.imageType = 'default';
    }

    const { parentElement } = (this.imageElement.nativeElement as HTMLElement).parentElement;

    // provide image ratio via css variable
    if (this.imageType !== 'default' && this.imageType !== 'original') {
      const ratioInPercent = `${(HUNDRED * sizes[this.imageType].height / sizes[this.imageType].width).toFixed(2)}%`;

      parentElement.style.setProperty('--ratio', ratioInPercent);
    }

    // regarding pixel density use half image size for css width

    if (this.imageType.includes('avatar') || this.imageType.includes('thumbnail')) {
      parentElement.style.setProperty('--width', `${sizes[this.imageType].width / 2}px`);
    }
  }
}
